import { useEffect, useMemo, useRef, useState } from 'react';

import AdminLayout from '@shared/components/organisms/AdminLayout/AdminLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import SearchFilter from '@shared/components/organisms/SearchFilter/SearchFilter';
import ApprovalDataTable from '@shared/components/organisms/ApprovalDataTable/ApprovalDataTable';
import Pagination from '@shared/components/organisms/Pagination/Pagination';
import Button from '@shared/components/atoms/Button/Button';
import { getDefaultDateRange } from '@shared/utils/formatDate';
import ApprovalStatus from '@shared/components/atoms/ApprovalStatus';
import useSort from '@shared/hooks/useSort';
import createExcelFile from '@shared/lib/excel';
import Alert from '@shared/components/atoms/Alert';
import {
  useDocumentSearchFiltersQuery,
  useAdminDocumentsQuery,
  useRemoveDocumentMutation,
} from '@features/document-management/hooks';
import DocumentDetailModal from '../../approval-box/components/DocumentDetailModal/DocumentDetailModal';

import styles from './DocumentsManagementPage.module.scss';

/**
 * DocumentsManagementPage
 * - 관리자 문서관리 페이지
 */
export default function DocumentsManagementPage() {
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [filterValues, setFilterValues] = useState({
    queryPeriod_start: defaultRange.start,
    queryPeriod_end: defaultRange.end,
    documentStatus: '',
    documentType: '',
    draftDepartment: '',
    documentTitle: '',
    drafter: '',
  });

  /** 검색 버튼 클릭 시 또는 초기 로드 시 적용된 검색 조건 (null이면 문서 조회 미실행) */
  const [appliedSearchParams, setAppliedSearchParams] = useState(null);
  const initialSearchDone = useRef(false);

  const { data: filterOptionsData } = useDocumentSearchFiltersQuery();
  const filterOptions = useMemo(
    () => ({
      teams: filterOptionsData?.teams ?? [],
      docTypes: filterOptionsData?.docTypes ?? [],
    }),
    [filterOptionsData],
  );

  const {
    data: tableData = [],
    isLoading: isDocumentsLoading,
    refetch: refetchDocuments,
  } = useAdminDocumentsQuery(appliedSearchParams, filterOptions);

  const removeMutation = useRemoveDocumentMutation();

  const loading = isDocumentsLoading || removeMutation.isPending;

  const [alertState, setAlertState] = useState({
    isOpen: false,
    mode: 'alert',
    message: '',
    onConfirm: null,
  });

  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  const { sortedData, sortField, sortDirection, handleSort } = useSort(tableData, {
    field: 'draftDate',
    direction: 'desc',
  });

  const paginatedData = useMemo(() => {
    return sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocIdx, setSelectedDocIdx] = useState(null);

  const isAllSelected = useMemo(
    () => tableData.length > 0 && selectedRows.length === tableData.length,
    [selectedRows, tableData],
  );

  /** 필터 옵션 로드 후 초기 검색 조건 적용 (1회만) */
  useEffect(
    function initAppliedSearchParams() {
      if (initialSearchDone.current) return;
      if (filterOptions.teams.length > 0 || filterOptions.docTypes.length > 0) {
        initialSearchDone.current = true;
        setAppliedSearchParams({
          queryPeriod_start: defaultRange.start,
          queryPeriod_end: defaultRange.end,
          documentStatus: '',
          documentType: '',
          draftDepartment: '',
          documentTitle: '',
          drafter: '',
        });
      }
    },
    [
      filterOptions.teams.length,
      filterOptions.docTypes.length,
      defaultRange.start,
      defaultRange.end,
    ],
  );

  // 필터 필드 정의
  const filterFields = useMemo(
    () => [
      // 1열: 조회기간, 문서상태
      {
        key: 'queryPeriod',
        type: 'dateRange',
        label: '조회기간',
        startPlaceholder: defaultRange.start,
        endPlaceholder: defaultRange.end,
        startKey: 'queryPeriod_start',
        endKey: 'queryPeriod_end',
        startValue: filterValues.queryPeriod_start || '',
        endValue: filterValues.queryPeriod_end || '',
      },
      {
        key: 'documentStatus',
        type: 'select',
        label: '문서상태',
        value: filterValues.documentStatus || '',
        options: [
          { label: '전체', value: '' },
          { label: '임시저장', value: 0 },
          { label: '결재대기', value: 1 },
          { label: '결재중', value: 2 },
          { label: '결재완료', value: 3 },
          { label: '반려', value: 4 },
          { label: '삭제', value: 5 },
          { label: '수정요청', value: 6 },
        ],
      },
      { newRow: true },
      // 2열: 문서유형, 기안부서
      {
        key: 'documentType',
        type: 'select',
        label: '문서유형',
        value: filterValues.documentType || '',
        options: ['전체', ...new Set(filterOptions.docTypes.map((d) => d.formName))],
        fill: true,
      },
      {
        key: 'draftDepartment',
        type: 'select',
        label: '기안부서',
        value: filterValues.draftDepartment || '',
        options: ['전체', ...new Set(filterOptions.teams.map((t) => t.teamName))],
        fill: true,
      },
      { newRow: true },
      // 3열: 문서제목, 기안자
      {
        key: 'documentTitle',
        type: 'input',
        label: '문서제목',
        placeholder: '',
        value: filterValues.documentTitle || '',
        wide: true,
      },
      {
        key: 'drafter',
        type: 'input',
        label: '기안자',
        placeholder: '',
        value: filterValues.drafter || '',
        fill: true,
      },
    ],
    [defaultRange, filterValues, filterOptions],
  );

  // 테이블 컬럼 정의
  const tableColumns = useMemo(
    () => [
      {
        key: 'checkbox',
        label: '',
        width: '60px',
        sortable: false,
        stopPropagation: true,
        headerRender() {
          return (
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={handleSelectAll}
              onClick={function (e) {
                e.stopPropagation();
              }}
              aria-label="전체 선택"
            />
          );
        },
        render(value, item) {
          const isDisabled = item.documentStatus === '삭제';

          return (
            <input
              type="checkbox"
              disabled={isDisabled}
              checked={!isDisabled && selectedRows.includes(item.id)}
              onClick={function (e) {
                e.stopPropagation();
              }}
              onChange={function () {
                handleRowSelect(item.id);
              }}
              aria-label="선택"
            />
          );
        },
      },
      {
        key: 'documentType',
        label: '문서유형',
        width: '150px',
        sortable: true,
      },
      {
        key: 'documentNumber',
        label: '문서번호',
        width: '200px',
        sortable: true,
      },
      {
        key: 'documentTitle',
        label: '문서제목',
        width: '400px',
        sortable: true,
      },
      {
        key: 'draftDate',
        label: '기안일자',
        width: '150px',
        sortable: true,
      },
      {
        key: 'draftDepartment',
        label: '기안부서',
        width: '150px',
        sortable: true,
      },
      {
        key: 'drafter',
        label: '기안자',
        width: '120px',
        sortable: true,
      },
      {
        key: 'documentStatus',
        label: '문서상태',
        width: '120px',
        sortable: true,
        render(value) {
          return (
            <div className={styles.statusCell}>
              <ApprovalStatus>{value}</ApprovalStatus>
            </div>
          );
        },
      },
      {
        key: 'is_corp_seal_used',
        label: '법인인감사용',
        width: '120px',
        sortable: true,
        render(value) {
          return value === 1 || value === '1' || value === true ? 'Y' : 'N';
        },
      },
    ],
    [selectedRows, isAllSelected],
  );
  // 2026.02.23 엑셀용 컬럼 생성 (법인인감사용은 corpSealUsed 키로 Y/N 출력)
  const excelColumns = useMemo(() => {
    return tableColumns
      .filter((col) => col.key !== 'checkbox')
      .map((col) =>
        col.key === 'is_corp_seal_used' ? { ...col, key: 'corpSealUsed', render: undefined } : col,
      );
  }, [tableColumns]);

  function handleFilterChange(key, value) {
    setFilterValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  function handleSearch() {
    setSelectedRows([]);
    setCurrentPage(1);
    setAppliedSearchParams({ ...filterValues });
  }

  // 필터 초기화 핸들러
  function handleReset() {
    // 1. 필터 UI 상태 초기화
    setFilterValues({
      queryPeriod_start: defaultRange.start,
      queryPeriod_end: defaultRange.end,
      documentStatus: '',
      documentType: '',
      draftDepartment: '',
      documentTitle: '',
      drafter: '',
    });

    // 2. 페이지 초기화
    setCurrentPage(1);

    // 3. 선택된 행 초기화
    setSelectedRows([]);
  }

  function handleExcel() {
    const formattedRows = tableData.map((row) => {
      const isCorpSealUsed =
        row.is_corp_seal_used === 1 ||
        row.is_corp_seal_used === '1' ||
        row.corpSealUsed === true ||
        row.corpSealUsed === 'Y';
      return {
        ...row,
        corpSealUsed: isCorpSealUsed ? 'Y' : 'N',
      };
    });

    createExcelFile({
      rows: formattedRows, // 전체 데이터 export
      fileName: '문서관리_리스트.xlsx',
      columns: excelColumns,
    });
  }

  function handleRowClick(item) {
    setSelectedDocIdx(item.id);
    setIsModalOpen(true);
  }

  function handleSelectAll() {
    if (isAllSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(tableData.map((item) => item.id));
    }
  }

  function handleRowSelect(id) {
    setSelectedRows(function (prev) {
      if (prev.includes(id)) {
        return prev.filter(function (rowId) {
          return rowId !== id;
        });
      }
      return [...prev, id];
    });
  }

  function handleConfirmDelete(rowsToDelete) {
    removeMutation.mutate(rowsToDelete, {
      onSuccess() {
        setAlertState({
          isOpen: true,
          mode: 'alert',
          message: '삭제되었습니다.',
          onConfirm: null,
        });
        setSelectedRows([]);
      },
      onError() {
        setAlertState({
          isOpen: true,
          mode: 'alert',
          message: '삭제 중 오류가 발생했습니다.',
          onConfirm: null,
        });
      },
    });
  }

  function handleDeleteSelected() {
    if (selectedRows.length === 0) {
      setAlertState({
        isOpen: true,
        mode: 'alert',
        message: '삭제할 항목을 선택해주세요.',
        onConfirm: null,
      });
      return;
    }

    // 진행중(2)·수정요청(6) 상태의 결재문서는 삭제 불가 → 선택에 섞여 있으면 알럿 후 중단
    const hasInProgress = tableData.some(
      (row) =>
        selectedRows.includes(row.id) &&
        (Number(row.stateCode) === 2 || Number(row.stateCode) === 6),
    );
    if (hasInProgress) {
      setAlertState({
        isOpen: true,
        mode: 'alert',
        message:
          '진행 중인 결재문서가 있습니다.\n진행 중·수정요청 상태의 문서는 삭제할 수 없습니다.',
        onConfirm: null,
      });
      return;
    }

    setAlertState({
      isOpen: true,
      mode: 'confirm',
      message: '선택한 문서를 삭제하시겠습니까?',
      onConfirm: () => handleConfirmDelete(selectedRows),
    });
  }

  // tableData 가 변경 될때 selectedRows정리
  useEffect(() => {
    setSelectedRows((prev) => prev.filter((id) => tableData.some((row) => row.id === id)));
  }, [tableData]);

  return (
    <AdminLayout>
      <TabContentLayout
        tabs={{
          label: '문서관리',
          content: (
            <ContentCard className={styles.contentCardMarginFix}>
              {/* 검색/필터 섹션 */}
              <div className={styles.filterWrapper}>
                <SearchFilter
                  fields={filterFields}
                  onFilterChange={handleFilterChange}
                  onSearch={handleSearch}
                  onReset={handleReset}
                  onExcel={handleExcel}
                  showExcelButton={true}
                  documnetCount={tableData.length}
                />
              </div>
              {/* 선택삭제 버튼 */}
              <div className={styles.buttonWrap}>
                <Button
                  type="button"
                  className={styles.deleteButton}
                  onClick={handleDeleteSelected}
                  disabled={selectedRows.length === 0}
                >
                  선택 삭제
                </Button>
              </div>
              {/* 데이터 테이블 */}
              <div className={styles.tableWrapper}>
                {loading ? (
                  <div className={styles.loadingCell}>데이터를 불러오는 중입니다...</div>
                ) : (
                  <>
                    <ApprovalDataTable
                      columns={tableColumns}
                      data={paginatedData}
                      onRowClick={handleRowClick}
                      onSort={handleSort}
                      sortField={{ field: sortField, direction: sortDirection }}
                      getRowKey={(item, index) => `${item.id}_${index}`}
                      emptyContent={<div className={styles.emptyCell}>문서가 없습니다.</div>}
                    />
                  </>
                )}
              </div>
              {/* 페이지네이션 */}
              <div className={styles.paginationWrapper}>
                {paginatedData.length > 0 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => setCurrentPage(page)}
                    onFirstPage={() => setCurrentPage(1)}
                    onPrevPage={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    onNextPage={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    onLastPage={() => setCurrentPage(totalPages)}
                  />
                )}
              </div>
            </ContentCard>
          ),
        }}
      />

      {/* 문서 상세 모달 */}
      {isModalOpen && (
        <DocumentDetailModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedDocIdx(null);
          }}
          docIdx={selectedDocIdx}
          userRole="viewer"
          tabType="all"
          isAdminContext={true}
          onSaveSuccess={refetchDocuments}
        />
      )}

      {/* 공용 알림/확인 모달 (휴가관리와 동일한 Alert 스타일) */}
      <Alert
        isOpen={alertState.isOpen}
        mode={alertState.mode}
        message={alertState.message}
        onConfirm={alertState.onConfirm}
        onClose={() =>
          setAlertState((prev) => ({
            ...prev,
            isOpen: false,
          }))
        }
      />
    </AdminLayout>
  );
}
